import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { repository } from '../../data'
import type { Machine } from '@kronos/domain'
import { Modal } from '../kit'
import { clock, problemOf } from './format'

const INSTALL = 'curl -fsSL https://tailscale.com/install.sh | sh'

/**
 * A one-time key that joins a device to the network as this machine. The key
 * is shown once and never stored; asking again makes a new one, which is the
 * whole recovery story for a key somebody lost.
 */
export function JoinDevice({ machine, onClose }: { machine: Machine; onClose: () => void }): JSX.Element {
  // One key per opening of the dialog: a query keyed on the moment it opened,
  // so React Query de-duplicates it (React's dev mode mounts twice) and never
  // refetches it. Not a mutation-in-an-effect: that loses its answer to the
  // discarded first mount and sits on "Making a key…" forever.
  const [opened] = useState(() => Date.now())
  const issue = useQuery({
    queryKey: ['join-key', machine.id, opened],
    queryFn: () => repository.joinKey(machine.id),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
  const problem = issue.error ? problemOf(issue.error) : ''

  const key = issue.data
  return (
    <Modal
      title={`Join a device as ${machine.name}`}
      subtitle="Run these on the machine itself, in a terminal. Password login is not touched."
      onClose={onClose}
    >
      <div className="stack">
        {issue.isPending ? <p className="muted">Making a key…</p> : null}
        {problem ? <p className="critical" role="alert">{problem}</p> : null}
        {key ? (
          <>
            <Step n={1} title="If Tailscale is not installed yet">
              <Command text={INSTALL} />
            </Step>
            <Step n={2} title="Join">
              <Command text={key.command} />
              <span className="muted">
                Works once, until {clock(key.expiresAt)}. It joins as {machine.tag}; the device keeps its own name
                and its own DNS.
              </span>
            </Step>
            <Step n={3} title="Check">
              <span className="muted">
                Close this dialog: within a minute the card shows the device online. Then give people access.
              </span>
            </Step>
          </>
        ) : null}
      </div>
    </Modal>
  )
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="stack" style={{ gap: 6 }}>
      <span className="field-label">
        {n}. {title}
      </span>
      {children}
    </section>
  )
}

function Command({ text }: { text: string }): JSX.Element {
  const [copied, setCopied] = useState(false)
  const copy = (): void => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <div className="access-command">
      <code className="access-command__text">{text}</code>
      <button onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  )
}
